import { inject, Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { UrlPrivilegeUtilService } from '../util-service/url-privilege-util.service';
import { Observable, of, catchError, map, takeUntil, Subject, take } from 'rxjs';
import { WhoAmI } from '../model/user/whoAmI.model';
import { UserService } from '../service/user/user-service';
import { UserContextService } from '../context/user.context.service';
import { LoginService } from '../service/user/login.service';
import { AuthorityUtil } from '../util/authority.util';
import { NavigationItemWithVisibility } from '../util-service/privileges';

@Injectable()
export class AuthGuard implements OnDestroy {
    private readonly _router = inject(Router);
    private readonly _userService = inject(UserService);
    private readonly _loginService = inject(LoginService);
    private readonly _userContextService = inject(UserContextService);
    private readonly _urlPrivilegeUtilService = inject(UrlPrivilegeUtilService);
    private readonly _destroy$: Subject<void> = new Subject();
    private _user!: WhoAmI | null;

    constructor() {
        this._userContextService
            .getContext()
            .pipe(takeUntil(this._destroy$))
            .subscribe((user: WhoAmI | null) => {
                this._user = user;
            });
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        this._userContextService.syncAuthCookieFromStorage();
        this._loginService.redirectUrl = state.url;
        return this._fetchUserContext().pipe(
            map((user: WhoAmI | null) => {
                this._user = user;

                // Validate session
                if (!user?.isActive()) {
                    this._userContextService.clear();
                    this._router.navigate(['/login']);
                    return false;
                }

                // Check for MFA flow
                if (user.hasAuthority(AuthorityUtil.ROLE_TEMP_MFA_ROLE)) {
                    this._router.navigate(['setup-mfa']);
                    return false;
                }

                // Check for general access
                const path = route.routeConfig?.path;
                const fullPath = path ? this.getFullPath(route) : path;
                const basicUrls = this._urlPrivilegeUtilService.getUserBasicUrls();

                if (basicUrls.includes(fullPath as string)) {
                    return true;
                }

                // Check for user-specific access based on roles and privileges
                if (this._checkAuthorityNested(this._urlPrivilegeUtilService.getAllURLPrivileges(), `/${fullPath}`)) {
                    return true;
                }

                // Prevent infinite loop - if already on dashboard or root, allow access
                if (!fullPath || fullPath === 'dashboard') {
                    return true;
                }

                this._router.navigate(['/dashboard']);
                return false;
            }),
            catchError(() => of(false))
        );
    }

    private getFullPath(route: ActivatedRouteSnapshot): string {
        const path = route.routeConfig?.path ?? '';

        if (route.parent?.routeConfig?.path) {
            return `${this.getFullPath(route.parent)}/${path}`;
        } else {
            return path;
        }
    }

    private _checkAuthorityNested(
        menus: NavigationItemWithVisibility[] | undefined,
        path: string | undefined
    ): boolean {
        if (!menus || !path) {
            return false;
        }

        for (const item of menus) {
            let containsModules = false;
            if (item?.modulesPaths && item.modulesPaths.length > 0) {
                containsModules = item.modulesPaths.includes(path);
            }
            if (item.routeLink === path || containsModules) {
                if (this._hasAuthority(item?.privileges)) {
                    return true;
                }
            }

            if (item.children && item.children.length > 0 && this._checkAuthorityNested(item.children, path)) {
                return true;
            }
        }

        return false;
    }

    private _hasAuthority(privileges: string[] | undefined): boolean {
        if (!privileges) {
            return false;
        }

        return privileges?.some((item: string) => this._user?.hasAuthority(item));
    }

    private _fetchUserContext(): Observable<WhoAmI | null> {
        const userToken = this._userContextService.getTokenFromStorage();
        return this._user && userToken
            ? of(this._user)
            : this._userService.whoAmI().pipe(
                  take(1),
                  catchError(() => {
                      return of(null);
                  })
              );
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
